import { apiErrorMessage } from '@agos/api-client'
import { formatCount, roleDisplayName, T, tt, useModalFocus } from '@agos/shared'
import { BUTTON_DANGER, BUTTON_SECONDARY, cn, useToast } from '@agos/ui'
import { Trash2 } from 'lucide-react'
import { useRef, useState } from 'react'

import {
  roleRefusalCode,
  useDeleteRole,
  useRolesOverview,
  type RolesOverview,
  type RoleView,
} from '../../roles/roles-api'
import { defaultMoveTarget, moveTargetWarning, orderRoles, roleNameOf, signedOutByMove } from '../role-model'

/**
 * *Zmazať rolu* - a custom role only, the main administrator's alone (`DELETE /settings/roles/{key}?moveTo=`,
 * §10A.408).
 *
 * **A role with people asks where they go first**, and says how many of them that signs out: a move to a role with
 * another base retires their sessions (§10A.406), a move within the same base does not. The default destination is
 * the built-in role the custom one works as, which moves everybody and signs nobody out - **except for a role based on
 * Administrátor** (`defaultMoveTarget`): the built-in Administrátor carries the pass (#58) a custom one does not, so it
 * is never preselected, and choosing it says what it hands the people moved.
 *
 * **The count is the fresh overview's, and the destination always goes with the delete.** The overview counts active
 * people while the server moves every holder, inactive ones included, and somebody may have joined since the dialog
 * opened; so the server's `ROLE_HAS_MEMBERS` (409) puts the destination picker in front of the reader with the server's
 * sentence and the roles read again, rather than leaving a dialog that sends the same refused delete for ever.
 */
export function DeleteRoleDialog({
  role,
  overview,
  onClose,
  onDeleted,
}: {
  role: RoleView
  overview: RolesOverview
  onClose: () => void
  onDeleted: () => void
}) {
  const panelRef = useRef<HTMLDivElement>(null)
  const { toast } = useToast()
  const remove = useDeleteRole()
  const roles = useRolesOverview()
  // The role as last read, not as the card that opened the dialog knew it.
  const fresh = roles.data ?? overview
  const current = fresh.roles.find((candidate) => candidate.key === role.key) ?? role
  const targets = orderRoles(fresh.roles).filter(
    (candidate) => candidate.key !== role.key && candidate.key !== 'main_admin',
  )
  const [moveTo, setMoveTo] = useState(() => defaultMoveTarget(role, targets))
  const [failure, setFailure] = useState('')
  // The server said the role has people the count did not show (inactive holders, or a newcomer).
  const [mustMove, setMustMove] = useState(false)
  const members = current.memberCount
  const asksWhere = members > 0 || mustMove
  const target = targets.find((candidate) => candidate.key === moveTo)
  const signedOut = signedOutByMove(current, target, members)
  const passWarning = moveTargetWarning(target)
  const { onKeyDown } = useModalFocus(true, panelRef, remove.isPending ? null : onClose)

  const confirm = () => {
    setFailure('')
    remove.mutate(
      // Always the destination when there is one: the server moves every holder with it and ignores it for nobody.
      { key: role.key, params: { moveTo: moveTo === '' ? null : moveTo } },
      {
        onSuccess: (result) => {
          toast({
            title: `${tt('Rola zmazaná')}: ${roleDisplayName(role)}`,
            description: `${tt('Presunutí')}: ${formatCount(result.moved)} · ${tt('odhlásení')}: ${formatCount(result.signedOut)}`,
          })
          onDeleted()
        },
        onError: (error: unknown) => {
          setFailure(apiErrorMessage(error, tt('Rolu sa nepodarilo zmazať.')))
          // People hold the role whom the count above did not show: ask where they go, and read the roles again.
          if (roleRefusalCode(error) === 'ROLE_HAS_MEMBERS') {
            setMustMove(true)
            void roles.refetch()
          }
        },
      },
    )
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-role-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={remove.isPending ? undefined : onClose}
    >
      <div
        ref={panelRef}
        onKeyDown={onKeyDown}
        onClick={(event) => event.stopPropagation()}
        className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4"
        data-delete-role-dialog={role.key}
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-red-500/10 text-red-500">
            <Trash2 className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h2 id="delete-role-title" className="text-base font-bold">
              {tt('Zmazať rolu')} {roleDisplayName(role)}?
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              <T>Jej práva a spravovanie zmiznú. V histórii ostane, čo mala.</T>
            </p>
          </div>
        </div>

        {asksWhere ? (
          <div className="space-y-2" data-delete-role-move>
            <label className="block space-y-1">
              <span className="block text-xs font-semibold">
                {members === 1
                  ? tt('Presunúť 1 človeka do')
                  : members > 1
                    ? `${tt('Presunúť')} ${formatCount(members)} ${tt('ľudí do')}`
                    : tt('Presunúť ľudí s touto rolou do')}
                :
              </span>
              <select
                value={moveTo}
                onChange={(event) => setMoveTo(event.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-background border border-border text-sm"
                data-delete-role-target
              >
                {moveTo === '' && (
                  <option value="" disabled>
                    {tt('Vyberte rolu...')}
                  </option>
                )}
                {targets.map((candidate) => (
                  <option key={candidate.key} value={candidate.key}>
                    {roleNameOf(overview.roles, candidate.key)}
                  </option>
                ))}
              </select>
            </label>
            <p
              className={cn('text-xs', signedOut > 0 ? 'text-amber-500' : 'text-muted-foreground')}
              data-delete-role-signed-out={signedOut}
            >
              {signedOut > 0
                ? `${tt('Odhlási sa')} ${formatCount(signedOut)} ${tt('z nich - nová rola pracuje ako iný základ, tak sa budú musieť znova prihlásiť.')}`
                : target === undefined
                  ? tt('Vyberte, kam ich presunúť.')
                  : tt('Nikto sa neodhlási - nová rola pracuje ako ten istý základ.')}
            </p>
            {passWarning !== null && (
              <p className="text-xs text-amber-500" data-delete-role-pass>
                {passWarning}
              </p>
            )}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground" data-delete-role-empty>
            {tt('Rolu nikto nemá, nikoho sa zmazanie nedotkne.')}
          </p>
        )}

        {failure !== '' && (
          <p className="text-xs text-red-500" role="alert" data-delete-role-error>
            {failure}
          </p>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={remove.isPending}
            data-dialog-cancel
            className={cn(BUTTON_SECONDARY, 'flex-1 px-4 py-2.5 rounded-xl text-sm')}
          >
            {tt('Zrušiť')}
          </button>
          <button
            type="button"
            onClick={confirm}
            disabled={remove.isPending || (asksWhere && moveTo === '')}
            className={cn(BUTTON_DANGER, 'flex-1 px-4 py-2.5 rounded-xl text-sm')}
            data-delete-role-confirm
          >
            {remove.isPending ? tt('Spracúvam...') : tt('Zmazať rolu')}
          </button>
        </div>
      </div>
    </div>
  )
}
